import React, { useMemo, useState } from 'react';
import { CheckPicker, DateRangePicker, Message, SelectPicker } from 'rsuite';
import { FilterField, FilterToolbar, renderSelectAllFooter } from '@nsi-advisory-services/components';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import { formatCurrency } from '../utils/currency';
import { getQuickDateRanges } from '../utils/dateRanges';
import {
  useTimesheetConfig,
  useTimesheetEmployees,
  useTimesheetReport,
  useTimesheetTasks,
} from '../hooks/useTimesheet';
import { ProviderBanner, TaskPicker, TimesheetNav } from '../components/timesheet/TimesheetParts';
import ReportExportModal from '../components/timesheet/ReportExportModal';
import {
  addDays,
  dateFromIso,
  formatDate,
  formatHours,
  isoFromDate,
  periodFor,
  previousPeriod,
  todayIso,
} from '../utils/timesheet';
import './Timesheet.css';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const GROUP_OPTIONS = [
  { value: 'project', label: 'Project' },
  { value: 'task', label: 'Task' },
  { value: 'client', label: 'Client' },
  { value: 'employee', label: 'Employee' },
  { value: 'billable', label: 'Billable / non-billable' },
  { value: 'rd', label: 'R&D credit / not' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const GROUP_LABEL = Object.fromEntries(GROUP_OPTIONS.map((o) => [o.value, o.label]));

// One-of-three filters, as pickers so they sit at the bar's height like the rest.
const BILLABLE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'billable', label: 'Billable' },
  { value: 'nonbillable', label: 'Non-billable' },
];
const RD_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'rd', label: 'Eligible' },
  { value: 'nonrd', label: 'Not R&D' },
];

// Validated pair (dataviz validator: lightness, chroma, CVD and contrast all
// pass on white). Billable is the first slot; the legend carries identity.
const SERIES = { billable: '#2a78d6', nonBillable: '#eb6834' };

const pct = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

// Cost, projected revenue and margin cells. The server only sends
// `financials` to payroll and admins, so their absence is the whole check.
// Hours left out of a figure for want of a rate are shown under it.
function MoneyCells({ f }) {
  if (!f) return null;
  const missing = (hours) => (hours > 0 ? <div className="nsi-table__muted small">{formatHours(hours)} h no rate</div> : null);
  return (
    <>
      <td className="nsi-table__right bdc-num">
        {f.costedHours > 0 ? formatCurrency(f.cost) : <span className="nsi-table__muted">No rate</span>}
        {f.costedHours > 0 ? missing(f.noCostHours) : null}
      </td>
      <td className="nsi-table__right bdc-num">
        {f.revenueHours > 0 ? formatCurrency(f.revenue) : <span className="nsi-table__muted">No rate</span>}
        {f.revenueHours > 0 ? missing(f.noRevenueHours) : null}
      </td>
      <td className={`nsi-table__right bdc-num${f.margin < 0 ? ' bdc-text-bad' : ''}`}>{formatCurrency(f.margin)}</td>
    </>
  );
}

// Daily totals rolled up to weeks or months once a day per column would be
// too thin to read.
const bucketDaily = (daily, start, end) => {
  const span = (dateFromIso(end) - dateFromIso(start)) / 86400000 + 1;
  const unit = span <= 62 ? 'day' : span <= 370 ? 'week' : 'month';
  const keyOf = (iso) => {
    if (unit === 'day') return iso;
    if (unit === 'month') return iso.slice(0, 7);
    const d = dateFromIso(iso);
    d.setDate(d.getDate() - d.getDay());
    return isoFromDate(d);
  };
  const map = new Map();
  if (unit === 'day') {
    for (let cur = start; cur <= end; cur = addDays(cur, 1)) map.set(cur, { key: cur, billable: 0, nonBillable: 0 });
  }
  daily.forEach((d) => {
    const key = keyOf(d.date);
    const row = map.get(key) || { key, billable: 0, nonBillable: 0 };
    row.billable += d.billableHours;
    row.nonBillable += d.hours - d.billableHours;
    map.set(key, row);
  });
  const rows = [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  const label = (key) => {
    if (unit === 'month') return dateFromIso(`${key}-01`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    if (unit === 'week') return `Wk ${formatDate(key)}`;
    return formatDate(key);
  };
  return { unit, rows: rows.map((r) => ({ ...r, label: label(r.key) })) };
};

/**
 * Timesheet reports: hours for any range, grouped and sub-grouped any way,
 * filtered by people, projects and billability, exportable as CSV.
 */
export default function TimesheetReportsPage() {
  const current = useMemo(() => periodFor(todayIso()), []);
  const [range, setRange] = useState([dateFromIso(current.start), dateFromIso(current.end)]);
  const [groupBy, setGroupBy] = useState('project');
  const [thenBy, setThenBy] = useState(null);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [taskIds, setTaskIds] = useState([]);
  const [billable, setBillable] = useState('all');
  const [rd, setRd] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());

  const configQuery = useTimesheetConfig();
  const config = configQuery.data;
  const installed = config?.installed !== false;
  const employeesQuery = useTimesheetEmployees({ enabled: installed });
  const tasksQuery = useTimesheetTasks({ includeArchived: true }, { enabled: installed });
  // The export dialog: a fresh one each time it opens, kept mounted while it fades out.
  const [exporting, setExporting] = useState(false);
  const [exportSession, setExportSession] = useState(0);
  const openExport = () => {
    setExportSession((n) => n + 1);
    setExporting(true);
  };

  const start = range?.[0] ? isoFromDate(range[0]) : null;
  const end = range?.[1] ? isoFromDate(range[1]) : null;
  const params = {
    start,
    end,
    groupBy,
    thenBy: thenBy || undefined,
    employeeIds,
    taskIds,
    billable: billable === 'all' ? undefined : billable,
    rd: rd === 'all' ? undefined : rd,
  };
  const reportQuery = useTimesheetReport(params, { enabled: installed });
  const report = reportQuery.data;

  const employees = useMemo(() => employeesQuery.data?.employees || [], [employeesQuery.data]);
  const employeeOptions = useMemo(
    () => employees.map((e) => ({ value: e.id, label: e.isActive ? e.name : `${e.name} (inactive)` })),
    [employees]
  );
  const tasks = useMemo(() => tasksQuery.data?.tasks || [], [tasksQuery.data]);
  const allTaskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);
  // A picked project covers everything under it, so "all" is every project.
  const projectIds = useMemo(() => tasks.filter((t) => !t.parentId).map((t) => t.id), [tasks]);
  const allProjectsPicked = projectIds.length > 0 && projectIds.every((id) => taskIds.includes(id));
  // The Employees picker's Select All / Deselect All footer, but judged on the
  // projects themselves: a count of picks would be fooled by picked tasks.
  const renderProjectsFooter = () => (
    <div className="nsi-fb-select-all">
      <button
        type="button"
        className="nsi-fb-link"
        disabled={projectIds.length === 0}
        onClick={() => setTaskIds(allProjectsPicked ? [] : projectIds)}
      >
        {allProjectsPicked ? 'Deselect All' : 'Select All'}
      </button>
    </div>
  );

  const quickRanges = useMemo(() => {
    const prev = previousPeriod(current);
    return [
      { label: 'This Pay Period', value: [dateFromIso(current.start), dateFromIso(current.end)] },
      { label: 'Last Pay Period', value: [dateFromIso(prev.start), dateFromIso(prev.end)] },
      ...getQuickDateRanges(),
    ];
  }, [current]);

  const chart = useMemo(() => (report ? bucketDaily(report.daily || [], report.start, report.end) : null), [report]);
  const chartData = useMemo(() => (chart ? {
    labels: chart.rows.map((r) => r.label),
    datasets: [
      {
        label: 'Billable',
        data: chart.rows.map((r) => Math.round(r.billable * 100) / 100),
        backgroundColor: SERIES.billable,
        maxBarThickness: 24,
        borderRadius: 0,
        stack: 'hours',
      },
      {
        label: 'Non-billable',
        data: chart.rows.map((r) => Math.round(r.nonBillable * 100) / 100),
        backgroundColor: SERIES.nonBillable,
        maxBarThickness: 24,
        // The data end is rounded; the join with the segment below is square,
        // and the white border is the 2px gap between the two.
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: 'bottom',
        borderColor: 'var(--dm-surface, #ffffff)',
        borderWidth: { bottom: 2 },
        stack: 'hours',
      },
    ],
  } : null), [chart]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, color: 'var(--dm-text-muted, #45474c)' } },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatHours(ctx.parsed.y)} h`,
          footer: (items) => `Total: ${formatHours(items.reduce((s, i) => s + i.parsed.y, 0))} h`,
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: 'var(--dm-text-faint, #75777d)', maxRotation: 0, autoSkip: true } },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#eceef0' },
        border: { display: false },
        ticks: { color: 'var(--dm-text-faint, #75777d)', callback: (v) => `${v} h` },
      },
    },
  }), []);

  const groups = report?.groups || [];
  const totalHours = report?.totals?.hours || 0;
  const showBudget = groupBy === 'project' || groupBy === 'task';
  const fin = report?.financials || null;
  const columnCount = 8 + (showBudget ? 1 : 0) + (fin ? 3 : 0);

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  // The filter bar as words, for the export dialog and the file's About.
  const exportFilters = (() => {
    const listOf = (names, none) => {
      if (names.length === 0) return none;
      return names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
    };
    const everyone = employees.length === 1
      ? employees[0].name
      : config?.permissions?.canReportAll ? 'Everyone' : 'You and the people you manage';
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    return [
      ['Employees', listOf(employeeIds.map((id) => employeeOptions.find((o) => o.value === id)?.label).filter(Boolean), everyone)],
      ['Projects / tasks', listOf(taskIds.map((id) => taskById.get(id)?.path).filter(Boolean), 'All projects')],
      ['Billable', BILLABLE_OPTIONS.find((o) => o.value === billable)?.label],
      ['R&D credit', RD_OPTIONS.find((o) => o.value === rd)?.label],
    ];
  })();

  return (
    <div className="bdc-page ts-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Timesheet', to: '/timesheet' }, { label: 'Reports' }]}
        icon="bi-bar-chart-line"
        title="Timesheet Reports"
        showBack={false}
        actions={(
          <>
            <button type="button" className="bdc-btn bdc-btn--sm ts-export-toggle" onClick={openExport} disabled={!installed}>
              <i className="bi bi-upload" aria-hidden="true" />
              Export
            </button>
            <TimesheetNav config={config} />
          </>
        )}
      />

      <ProviderBanner config={config} />

      <FilterToolbar>
        <FilterField label="Dates" basis={215} minWidth={260}>
          <DateRangePicker
            value={range}
            onChange={(value) => setRange(value || [dateFromIso(current.start), dateFromIso(current.end)])}
            format="MM/dd/yyyy"
            character=" – "
            isoWeek={false}
            weekStart={0}
            ranges={quickRanges}
            cleanable={false}
            preventOverflow
            block
          />
        </FilterField>
        {/* Whoever the server lets this person see: everyone for a reporter,
            themselves and the people they manage for a coach, or just
            themselves - in which case the field is locked to them. */}
        {employees.length > 1 ? (
          <FilterField label="Employees" basis={260} minWidth={180}>
            <CheckPicker
              data={employeeOptions}
              countable={false}
              renderValue={renderEmployeeSelection(employeeOptions)}
              renderExtraFooter={renderSelectAllFooter(employeeOptions, employeeIds, setEmployeeIds)}
              value={employeeIds}
              onChange={(value) => setEmployeeIds(value || [])}
              placeholder={config?.permissions?.canReportAll ? 'Everyone' : 'You and the people you manage'}
              preventOverflow
              block
            />
          </FilterField>
        ) : employees.length === 1 ? (
          <FilterField label="Employees" basis={260} minWidth={180}>
            <CheckPicker
              data={employeeOptions}
              value={[employees[0].id]}
              cleanable={false}
              disabled
              block
              title="These reports cover your own time."
            />
          </FilterField>
        ) : null}
        {/* The bar shares its width by these bases. The groupings and dates
            hold short values, so they give room to the two pickers. */}
        <FilterField label="Group by" basis={125} minWidth={130}>
          <SelectPicker
            data={GROUP_OPTIONS}
            value={groupBy}
            onChange={(value) => {
              setGroupBy(value || 'project');
              if (value === thenBy) setThenBy(null);
              setExpanded(new Set());
            }}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>
        <FilterField label="Then by" basis={125} minWidth={130}>
          <SelectPicker
            data={GROUP_OPTIONS.filter((o) => o.value !== groupBy)}
            value={thenBy}
            onChange={(value) => {
              setThenBy(value || null);
              setExpanded(new Set());
            }}
            placeholder="Nothing"
            searchable={false}
            block
          />
        </FilterField>
        <FilterField label="Projects / tasks" basis={280} minWidth={180}>
          {/* The timesheet's own picker. Archived and time-off tasks are
              offered too: a report reaches back to old work. */}
          <TaskPicker
            tasks={tasks}
            value={taskIds}
            onChange={(value) => setTaskIds(value || [])}
            includeIds={allTaskIds}
            multiple
            placeholder="All projects"
            renderExtraFooter={renderProjectsFooter}
          />
        </FilterField>
        <FilterField label="Billable" basis={120} minWidth={130}>
          <SelectPicker
            data={BILLABLE_OPTIONS}
            value={billable}
            onChange={(value) => setBillable(value || 'all')}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>
        <FilterField label="R&amp;D credit" basis={120} minWidth={130}>
          <SelectPicker
            data={RD_OPTIONS}
            value={rd}
            onChange={(value) => setRd(value || 'all')}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>
      </FilterToolbar>

      {reportQuery.error ? <Message type="error" showIcon>{String(reportQuery.error?.message || reportQuery.error)}</Message> : null}
      {report?.truncated ? (
        <Message type="warning" showIcon>This range has more than 20,000 entries; only the first 20,000 are counted. Narrow the range.</Message>
      ) : null}

      {fin && !fin.revenueStatus?.available ? (
        <Message type="warning" showIcon>{fin.revenueStatus.message}</Message>
      ) : null}
      {fin && (fin.noCostHours > 0 || fin.noRevenueHours > 0) ? (
        <Message type="info" showIcon>
          Some hours have no rate and are left out of the money columns, so read them as low:
          {fin.noCostHours > 0 ? ` ${formatHours(fin.noCostHours)} h have no pay rate in force (cost).` : ''}
          {fin.noRevenueHours > 0
            ? ` ${formatHours(fin.noRevenueHours)} h have no projected revenue (${(fin.noRevenueBreakdown || []).map((b) => `${b.label}: ${formatHours(b.hours)} h`).join('; ')}).`
            : ''}
        </Message>
      ) : null}

      <Section title="Hours over time" note={chart ? `by ${chart.unit}` : null}>
        <div className="ts-chart">
          {chartData && report?.totals?.entries ? (
            <Bar data={chartData} options={chartOptions} aria-label="Billable and non-billable hours over time" role="img" />
          ) : (
            <div className="ts-empty h-100 justify-content-center">
              <i className="bi bi-bar-chart" aria-hidden="true" />
              {reportQuery.isLoading ? 'Loading…' : 'No time logged in this range.'}
            </div>
          )}
        </div>
      </Section>

      <div className="nsi-table-wrap">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <h3 className="bdc-table-head__title">By {GROUP_LABEL[groupBy].toLowerCase()}{thenBy ? `, then ${GROUP_LABEL[thenBy].toLowerCase()}` : ''}</h3>
            <span className="bdc-count">{groups.length}</span>
          </div>
          {thenBy && groups.length ? (
            <div className="bdc-table-head__actions">
              <button
                type="button"
                className="bdc-btn bdc-btn--sm"
                onClick={() => setExpanded(expanded.size === groups.length ? new Set() : new Set(groups.map((g) => g.key)))}
              >
                {expanded.size === groups.length ? 'Collapse all' : 'Expand all'}
              </button>
            </div>
          ) : null}
        </div>
        <table className="nsi-table">
          <thead>
            <tr>
              <th>{GROUP_LABEL[groupBy]}</th>
              <th className="nsi-table__right">Hours</th>
              <th className="nsi-table__right">Billable</th>
              <th className="nsi-table__right">Billable %</th>
              <th className="nsi-table__right">R&amp;D</th>
              <th className="nsi-table__right">Share</th>
              <th className="nsi-table__right">Entries</th>
              <th className="nsi-table__right">People</th>
              {fin ? (
                <>
                  <th className="nsi-table__right">Cost</th>
                  <th className="nsi-table__right">Revenue</th>
                  <th className="nsi-table__right">Margin</th>
                </>
              ) : null}
              {showBudget ? <th>Budget (all time)</th> : null}
            </tr>
          </thead>
          <tbody>
            {reportQuery.isLoading ? (
              <tr><td colSpan={columnCount} className="nsi-table__empty">Loading…</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={columnCount} className="nsi-table__empty">No time logged in this range.</td></tr>
            ) : groups.map((g) => {
              const isOpen = expanded.has(g.key);
              const over = g.budgetHours != null && g.allTimeHours > g.budgetHours;
              return (
                <React.Fragment key={g.key}>
                  <tr>
                    <td>
                      {thenBy ? (
                        <button type="button" className="ts-expander" aria-expanded={isOpen} onClick={() => toggle(g.key)}>
                          <i className="bi bi-chevron-right" aria-hidden="true" />
                          <span className="fw-semibold">{g.label}</span>
                        </button>
                      ) : <span className="fw-semibold">{g.label}</span>}
                      {g.code || g.clientName ? (
                        <div className="nsi-table__muted small">{[g.code, g.clientName].filter(Boolean).join(' · ')}</div>
                      ) : null}
                    </td>
                    <td className="nsi-table__right bdc-num fw-semibold">{formatHours(g.hours)}</td>
                    <td className="nsi-table__right bdc-num">{formatHours(g.billableHours)}</td>
                    <td className="nsi-table__right bdc-num">{pct(g.billableHours, g.hours)}</td>
                    <td className="nsi-table__right bdc-num">{formatHours(g.rdHours)}</td>
                    <td className="nsi-table__right bdc-num">{pct(g.hours, totalHours)}</td>
                    <td className="nsi-table__right bdc-num">{g.entries}</td>
                    <td className="nsi-table__right bdc-num">{g.employees}</td>
                    <MoneyCells f={g.financials} />
                    {showBudget ? (
                      <td>
                        {g.budgetHours != null ? (
                          <div className="ts-budget">
                            <div className="ts-budget__track">
                              <div
                                className={`ts-budget__fill${over ? ' ts-budget__fill--over' : ''}`}
                                style={{ width: `${Math.min(100, g.budgetHours > 0 ? (g.allTimeHours / g.budgetHours) * 100 : 100)}%` }}
                              />
                            </div>
                            <span className={`ts-budget__label${over ? ' bdc-text-bad' : ''}`}>
                              {formatHours(g.allTimeHours)} of {formatHours(g.budgetHours)} h{over ? ' — over budget' : ''}
                            </span>
                          </div>
                        ) : <span className="nsi-table__muted">—</span>}
                      </td>
                    ) : null}
                  </tr>
                  {isOpen ? (g.children || []).map((c) => (
                    <tr key={`${g.key}|${c.key}`} className="ts-child-row">
                      <td>{c.label}</td>
                      <td className="nsi-table__right bdc-num">{formatHours(c.hours)}</td>
                      <td className="nsi-table__right bdc-num">{formatHours(c.billableHours)}</td>
                      <td className="nsi-table__right bdc-num">{pct(c.billableHours, c.hours)}</td>
                      <td className="nsi-table__right bdc-num">{formatHours(c.rdHours)}</td>
                      <td className="nsi-table__right bdc-num">{pct(c.hours, g.hours)}</td>
                      <td className="nsi-table__right bdc-num">{c.entries}</td>
                      <td className="nsi-table__right bdc-num">{c.employees}</td>
                      <MoneyCells f={c.financials} />
                      {showBudget ? <td /> : null}
                    </tr>
                  )) : null}
                </React.Fragment>
              );
            })}
          </tbody>
          {groups.length ? (
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="nsi-table__right bdc-num">{formatHours(totalHours)}</td>
                <td className="nsi-table__right bdc-num">{formatHours(report.totals.billableHours)}</td>
                <td className="nsi-table__right bdc-num">{pct(report.totals.billableHours, totalHours)}</td>
                <td className="nsi-table__right bdc-num">{formatHours(report.totals.rdHours)}</td>
                <td className="nsi-table__right bdc-num">100%</td>
                <td className="nsi-table__right bdc-num">{report.totals.entries}</td>
                <td className="nsi-table__right bdc-num">{report.totals.employees}</td>
                <MoneyCells f={fin} />
                {showBudget ? <td /> : null}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {exportSession > 0 ? (
        <ReportExportModal
          key={exportSession}
          open={exporting}
          onClose={() => setExporting(false)}
          range={range}
          quickRanges={quickRanges}
          params={params}
          groupBy={groupBy}
          thenBy={thenBy}
          groupLabels={GROUP_LABEL}
          filters={exportFilters}
          withMoney={Boolean(fin)}
          entryCount={report?.totals?.entries ?? null}
        />
      ) : null}
    </div>
  );
}
